'use client';

import { LandingBreadcrumbs } from '@/components/landing/breadcrumbs';

export default function DocumentationPage() {
  return (
    <main className="flex-1 min-h-[calc(100vh-64px)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <LandingBreadcrumbs
          items={[
            { label: 'Help', href: '/help' },
            { label: 'Documentation' },
          ]}
        />

        <div className="prose prose-sm max-w-none">
          <h1 className="text-4xl font-bold mb-6">Introduction to Rudrise</h1>

          <p className="text-lg text-muted-foreground mb-8">
            Welcome to the Rudrise documentation. Rudrise is a modern, web-based
            LaTeX editor designed for researchers, students, and professionals to
            write and collaborate on technical documents with ease.
          </p>

          {/* Getting Started Section */}
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              New to LaTeX?
            </h3>
            <p className="text-muted-foreground">
              If you&apos;re new to LaTeX, we recommend starting with our{' '}
              <a
                href="/documentation"
                className="text-primary hover:underline"
              >
                LaTeX Crash Course
              </a>{' '}
              before diving into the editor specifics.
            </p>
          </div>

          <h2 className="text-2xl font-bold mt-12 mb-4">
            What makes Rudrise different?
          </h2>

          <p className="text-muted-foreground mb-6">
            Traditional LaTeX editors require complex local setups, package
            installations, and steep learning curves. Rudrise eliminates these
            hurdles by providing a cloud-first environment that works right in
            your browser.
          </p>

          <h3 className="text-lg font-semibold mt-8 mb-3">Zero Setup</h3>
          <p className="text-muted-foreground mb-6">
            Start writing instantly without downloading gigabytes of TeX
            distributions.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-3">
            Real-time Collaboration
          </h3>
          <p className="text-muted-foreground mb-6">
            Edit the same document simultaneously with your peers.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-3">
            Smart Auto-completion
          </h3>
          <p className="text-muted-foreground mb-6">
            Context-aware suggestions for commands, environments, and citations.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-3">Instant Preview</h3>
          <p className="text-muted-foreground mb-6">
            Side-by-side PDF rendering that updates as you type.
          </p>

          {/* Code Example */}
          <div className="bg-gray-900 text-white rounded-lg p-6 overflow-x-auto my-8 font-mono text-sm">
            <p className="text-green-400 mb-2">$ main.tex</p>
            <pre className="text-gray-300">{`\\documentclass{article}\\usepackage{utf8}{inputenc}
\\title{My First Document}\\author{Rudrise User}\\date{\\today}
\\begin{document}
\\maketitle
\\section{Introduction} Welcome to Rudrise! This is a simple LaTeX document.
\\end{document}`}</pre>
          </div>

          <p className="text-muted-foreground">
            Once you paste this code into your editor, the PDF will compile
            automatically on the right side of your screen.
          </p>

          {/* Next Steps */}
          <div className="border-t border-border pt-8 mt-12">
            <h2 className="text-2xl font-bold mb-4">Your First Document</h2>
            <p className="text-muted-foreground mb-6">
              To create a new project, click the &quot;New Project&quot; button
              in your dashboard. You can choose from hundreds of templates (e.g.,
              IEEE, ACM, Springer) or start with a blank document.
            </p>

            <p className="text-sm text-muted-foreground">
              <strong>Last updated:</strong> Mar 09, 2026
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
